import { FileInfo, CodedProperty, NamedProperty } from "sprint-common-web";

import { AspectRatio } from "../../../../data/lookup-data-providers/AspectRatiosDataProvider";
import { AspectType } from "../../../../data/lookup-data-providers/AspectTypesDataProvider";
import { ContentDetails } from "../../../../types/ContentDetails";
import { CourseOfActionReference } from "../../../../types/CourseOfActionReference";
import { DynamicSubtitleType } from "../../../../types/DynamicSubtitleType";
import { FixedBillingReference } from "../../../../types/FixedBillingReferfence";
import { RecordType } from "../../../../types/RecordType";
import { LanguageReference } from "../../../../types/references/LanguageReference";
import { RejectionReasonReference } from "../../../../types/RejectionReasonReference";
import { Standard } from "../../../../types/Standard";
import { StorageType } from "../../../../types/StorageType";

export const CleanVideoSubtitleId = "clean-video-id";

export enum EmbeddedSubtitleType {
    Subtitle = 1,
    CleanVideo = 2
}

export interface Subtitle {
    language: LanguageReference;
}

export type CleanVideo = { type: EmbeddedSubtitleType.CleanVideo };
export type VideoWithSubtitle = Subtitle & { type: EmbeddedSubtitleType.Subtitle };

export type EmbeddedSubtitle = VideoWithSubtitle | CleanVideo;

export type OriginatorReference = NamedProperty;
export type FileRecordRejection = { rejectionReason: RejectionReasonReference; rejectionDetails?: string };

export type GetFileRecordDetailsResponse =
    | VideoFileRecordDetailsResponse
    | AudioFileRecordDetailsResponse
    | SubtitleFileRecordDetailsResponse;

export interface FileRecordDeliveryInfo {
    id: string;
    file: FileInfo;
    originator: OriginatorReference;
    arrivalDate: string;
    reference?: string;
    path?: string;
}

export type FileRecordLibrary = Omit<FileRecordDeliveryInfo, "file"> & {
    fileName: string;
    storageType: StorageType;
};

type FileRecordRejectionDetailsResponse = {
    reason: RejectionReasonReference;
    details?: string;
    courseOfAction?: CourseOfActionReference;
    fixedBilling?: FixedBillingReference;
};

type FileRecordWaitingForClientResponseDetailsResponse = {
    details?: string;
    courseOfAction?: CourseOfActionReference;
    fixedBilling?: FixedBillingReference;
};

export type FileRecordStatusDetailsResponse =
    | FileRecordRejectionDetailsResponse
    | FileRecordWaitingForClientResponseDetailsResponse;

export const isFileRecordRejectionDetailsResponse = (
    statusDetails: FileRecordStatusDetailsResponse
): statusDetails is FileRecordRejectionDetailsResponse => {
    return "reason" in statusDetails;
};

export interface BaseFileRecordDetailsResponse {
    id: string;
    status: number;
    statusDetails?: FileRecordStatusDetailsResponse;
    delivery: FileRecordDeliveryInfo;
    content?: ContentDetails;
    storageType: StorageType;
    isNotificationApproved: boolean;
    isTrailer: boolean;
}

export interface VideoFileRecordDetailsResponse extends BaseFileRecordDetailsResponse {
    recordType: RecordType.Video;
    metadata: VideoFileRecordMetadataResponse;
    soundtracks: CodedProperty[];
    subtitle?: EmbeddedSubtitle;
}

export interface VideoFileRecordMetadataResponse {
    duration?: string;
    framerate?: string;
    aspectRatio?: AspectRatio;
    aspectType?: AspectType;
    encoding?: boolean;
    videoBitRate?: number;
    resolution?: string;
    standard?: Standard;
}

export interface AudioFileRecordDetailsResponse extends BaseFileRecordDetailsResponse {
    recordType: RecordType.Audio;
    metadata: AudioFileRecordMetadataResponse;
    soundtrack?: CodedProperty;
}

export interface AudioFileRecordMetadataResponse {
    audioFormat?: string;
    resolution?: string;
    duration?: string;
    sampleRate?: number;
    channelsCount?: number;
    audioBitRate?: number;
}

export interface SubtitleFileRecordDetailsResponse extends BaseFileRecordDetailsResponse {
    recordType: RecordType.Subtitle;
    subtitleType: DynamicSubtitleType;
    subtitle?: CodedProperty;
}

export const isVideoFileRecordDetails = (
    file: GetFileRecordDetailsResponse
): file is VideoFileRecordDetailsResponse => {
    return file.recordType === RecordType.Video;
};

export const isAudioFileRecordDetails = (
    file: GetFileRecordDetailsResponse
): file is AudioFileRecordDetailsResponse => {
    return file.recordType === RecordType.Audio;
};

export const isSubtitleFileRecordDetails = (
    file: GetFileRecordDetailsResponse
): file is SubtitleFileRecordDetailsResponse => {
    return file.recordType === RecordType.Subtitle;
};

export const isVideoWithSubtitle = (subtitle: EmbeddedSubtitle): subtitle is VideoWithSubtitle => {
    return "language" in subtitle;
};
